(() => {
  if (window.__LAF_BUNDLE_GUARD__) return;
  window.__LAF_BUNDLE_GUARD__ = true;

  const routesRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const nativeFetch = window.fetch.bind(window);
  const mutationPattern = /\/cart\/(?:add|change|update|clear)\.js(?:\?|$)/i;
  let running = false;
  let queued = false;
  let timer = null;

  const packNumber = (value) => {
    const match = String(value || '').match(/Pack\s*(1|4|9)/i);
    return match ? Number(match[1]) : 0;
  };

  const cloneProperties = (item) => {
    const source = item && item.properties && typeof item.properties === 'object'
      ? item.properties
      : {};
    return { ...source };
  };

  const sameProperties = (a, b) => {
    const left = Object.entries(a || {}).filter(([, value]) => value !== null && value !== undefined && value !== '');
    const right = Object.entries(b || {}).filter(([, value]) => value !== null && value !== undefined && value !== '');
    if (left.length !== right.length) return false;
    left.sort(([aKey]) => aKey.localeCompare(aKey));
    right.sort(([aKey]) => aKey.localeCompare(aKey));
    return left.every(([key, value], index) => key === right[index][0] && String(value) === String(right[index][1]));
  };

  const readCart = async () => {
    const response = await nativeFetch(routesRoot + 'cart.js', {
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin',
      cache: 'no-store'
    });
    if (!response.ok) throw new Error('LAF_CART_READ_FAILED');
    return response.json();
  };

  const writeProperties = async (line, quantity, properties) => {
    const response = await nativeFetch(routesRoot + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin',
      body: JSON.stringify({ line, quantity, properties })
    });
    if (!response.ok) throw new Error('LAF_CART_CHANGE_FAILED');
    return response.json();
  };

  const reconcile = async () => {
    if (running) {
      queued = true;
      return;
    }

    running = true;
    let changed = false;

    try {
      const cart = await readCart();
      const items = Array.isArray(cart && cart.items) ? cart.items : [];
      const groups = new Map();

      items.forEach((item, index) => {
        const properties = cloneProperties(item);
        const bundleId = String(properties._laf_bundle_id || '').trim();
        const originalPack =
          packNumber(properties._laf_pack_original) ||
          packNumber(properties._laf_pack);
        if (!bundleId || !originalPack) return;

        if (!groups.has(bundleId)) {
          groups.set(bundleId, {
            bundleId,
            expected: Number(properties._laf_expected_qty) || originalPack,
            pack: originalPack,
            total: 0,
            rows: [],
            gifts: { keychain: '', bag: '', towel: '' }
          });
        }

        const group = groups.get(bundleId);
        group.total += Math.max(0, Number(item.quantity) || 0);
        group.rows.push({ item, index, properties });

        group.gifts.keychain =
          group.gifts.keychain ||
          String(properties._laf_gift_keychain || properties['Cadeau inclus'] || '');
        group.gifts.bag =
          group.gifts.bag ||
          String(properties._laf_gift_bag || properties['Sac offert'] || '');
        group.gifts.towel =
          group.gifts.towel ||
          String(properties._laf_gift_towel || properties['Serviette offerte'] || '');
      });

      // Recovery for a Shopify edge case: a single valid pack can be split into
      // multiple bundle IDs (for example 2 + 2 lines for a Pack 4). In that case,
      // recover the pack only when incomplete groups with the exact same pack and
      // gift choices add up to exactly one expected pack. This keeps the original
      // anti-gift guard intact for genuinely incomplete packs.
      const recoveryBuckets = new Map();

      for (const group of groups.values()) {
        if (group.pack <= 1 || group.total >= group.expected) continue;

        const recoveryKey = [
          group.pack,
          group.expected,
          group.gifts.keychain || '',
          group.gifts.bag || '',
          group.gifts.towel || ''
        ].join('||');

        if (!recoveryBuckets.has(recoveryKey)) {
          recoveryBuckets.set(recoveryKey, []);
        }
        recoveryBuckets.get(recoveryKey).push(group);
      }

      for (const bucket of recoveryBuckets.values()) {
        if (bucket.length < 2) continue;
        const recoveredTotal = bucket.reduce((sum, group) => sum + group.total, 0);

        if (recoveredTotal === bucket[0].expected) {
          bucket.forEach((group, index) => {
            group.recoveredComplete = true;
            group.recoveryLeader = index === 0;
          });
        }
      }

      for (const group of groups.values()) {
        const complete = group.total >= group.expected || group.recoveredComplete === true;
        const canonicalPack = 'Pack ' + group.pack;

        for (let rowIndex = 0; rowIndex < group.rows.length; rowIndex += 1) {
          const row = group.rows[rowIndex];
          const next = { ...row.properties };

          next._laf_pack_original = canonicalPack;
          next._laf_expected_qty = String(group.expected);

          if (group.gifts.keychain) next._laf_gift_keychain = group.gifts.keychain;
          if (group.gifts.bag) next._laf_gift_bag = group.gifts.bag;
          if (group.gifts.towel) next._laf_gift_towel = group.gifts.towel;

          if (complete) {
            next._laf_pack = canonicalPack;

            const showBuyerFacingGifts =
              rowIndex === 0 &&
              group.pack > 1 &&
              (group.recoveredComplete !== true || group.recoveryLeader === true);

            if (showBuyerFacingGifts) {
              if (group.gifts.keychain) next['Cadeau inclus'] = group.gifts.keychain;
              if (group.gifts.bag) next['Sac offert'] = group.gifts.bag;
              if (group.pack === 9 && group.gifts.towel) next['Serviette offerte'] = group.gifts.towel;
            } else {
              delete next['Cadeau inclus'];
              delete next['Sac offert'];
              delete next['Serviette offerte'];
            }
          } else {
            // Exact Flow checks for "Pack 4" / "Pack 9" will no longer match
            // while this bundle is incomplete.
            next._laf_pack = canonicalPack + ' incomplet';
            delete next['Cadeau inclus'];
            delete next['Sac offert'];
            delete next['Serviette offerte'];
          }

          if (!sameProperties(row.properties, next)) {
            // Re-read line positions after each write because a Shopify cart line key
            // can change when its properties change.
            const fresh = await readCart();
            const freshItems = Array.isArray(fresh && fresh.items) ? fresh.items : [];
            const lineIndex = freshItems.findIndex((candidate) => {
              const candidateProps = cloneProperties(candidate);
              return String(candidateProps._laf_bundle_id || '') === group.bundleId &&
                Number(candidate.variant_id) === Number(row.item.variant_id) &&
                String(candidateProps._laf_pack_original || candidateProps._laf_pack || '').match(new RegExp('Pack\\s*' + group.pack, 'i'));
            });

            if (lineIndex >= 0) {
              await writeProperties(
                lineIndex + 1,
                Number(freshItems[lineIndex].quantity) || 0,
                next
              );
              changed = true;
            }
          }
        }
      }

      if (changed) {
        document.dispatchEvent(new CustomEvent('laf:bundle-guard-updated'));

        if (/\/cart(?:\/|$|\?)/.test(window.location.pathname + window.location.search)) {
          const reloadKey = 'laf_bundle_guard_reload';
          if (!sessionStorage.getItem(reloadKey)) {
            sessionStorage.setItem(reloadKey, '1');
            window.location.reload();
            return;
          }
        } else {
          // The cart drawer may have rendered the response from the customer's
          // original quantity change just before this guard fixed the bundle
          // properties. Refresh it once so prices/discounts shown in the drawer
          // match the corrected server-side cart.
          const drawerItems = document.querySelector('cart-drawer-items');
          if (drawerItems && typeof drawerItems.onCartUpdate === 'function') {
            await drawerItems.onCartUpdate();
          }
        }
      } else {
        sessionStorage.removeItem('laf_bundle_guard_reload');
      }
    } catch (error) {
      console.warn('[LAF] Bundle guard:', error && error.message ? error.message : error);
    } finally {
      running = false;
      if (queued) {
        queued = false;
        schedule(80);
      }
    }
  };

  const schedule = (delay = 180) => {
    clearTimeout(timer);
    timer = setTimeout(reconcile, delay);
  };

  window.fetch = async (...args) => {
    const response = await nativeFetch(...args);
    try {
      const requestUrl = String(
        typeof args[0] === 'string' ? args[0] : (args[0] && args[0].url) || ''
      );
      if (!running && mutationPattern.test(requestUrl)) schedule();
    } catch (_) {}
    return response;
  };

  document.addEventListener('change', (event) => {
    if (event.target && event.target.closest && event.target.closest('cart-items, cart-drawer, form[action*="/cart"]')) {
      schedule(250);
    }
  }, true);

  document.addEventListener('click', (event) => {
    if (event.target && event.target.closest && event.target.closest('cart-remove-button, [data-cart-remove], cart-items button, cart-drawer button')) {
      schedule(300);
    }
  }, true);


  // Checkout barrier: never let Shopify create the order while bundle
  // properties are still stale after a customer removes a pair.
  const waitForIdle = async () => {
    while (running) {
      await new Promise((resolve) => setTimeout(resolve, 25));
    }
  };

  const hasUnsafeIncompleteBundle = (cart) => {
    const items = Array.isArray(cart && cart.items) ? cart.items : [];
    const groups = new Map();

    items.forEach((item) => {
      const properties = cloneProperties(item);
      const bundleId = String(properties._laf_bundle_id || '').trim();
      const originalPack =
        packNumber(properties._laf_pack_original) ||
        packNumber(properties._laf_pack);
      if (!bundleId || !originalPack || originalPack === 1) return;

      if (!groups.has(bundleId)) {
        groups.set(bundleId, {
          expected: Number(properties._laf_expected_qty) || originalPack,
          total: 0,
          rows: []
        });
      }

      const group = groups.get(bundleId);
      group.total += Math.max(0, Number(item.quantity) || 0);
      group.rows.push(properties);
    });

    const recoveryBuckets = new Map();

    for (const group of groups.values()) {
      if (group.total >= group.expected) continue;

      const sample = group.rows[0] || {};
      const pack = packNumber(sample._laf_pack_original) || packNumber(sample._laf_pack);
      const recoveryKey = [
        pack,
        group.expected,
        String(sample._laf_gift_keychain || sample['Cadeau inclus'] || ''),
        String(sample._laf_gift_bag || sample['Sac offert'] || ''),
        String(sample._laf_gift_towel || sample['Serviette offerte'] || '')
      ].join('||');

      if (!recoveryBuckets.has(recoveryKey)) recoveryBuckets.set(recoveryKey, []);
      recoveryBuckets.get(recoveryKey).push(group);
    }

    const recovered = new Set();

    for (const bucket of recoveryBuckets.values()) {
      if (bucket.length < 2) continue;
      const total = bucket.reduce((sum, group) => sum + group.total, 0);
      if (total === bucket[0].expected) {
        bucket.forEach((group) => recovered.add(group));
      }
    }

    return Array.from(groups.values()).some((group) => {
      if (group.total >= group.expected || recovered.has(group)) return false;
      return group.rows.some((properties) => {
        const pack = String(properties._laf_pack || '').trim();
        return /^Pack\\s*(4|9)$/i.test(pack) ||
          Boolean(properties['Cadeau inclus']) ||
          Boolean(properties['Sac offert']) ||
          Boolean(properties['Serviette offerte']);
      });
    });
  };

  const prepareCheckout = async () => {
    clearTimeout(timer);
    await waitForIdle();
    await reconcile();
    await waitForIdle();

    const verifiedCart = await readCart();
    if (hasUnsafeIncompleteBundle(verifiedCart)) {
      // One extra reconciliation covers a cart mutation that landed at the
      // exact same time as the first verification.
      await reconcile();
      await waitForIdle();
      const finalCart = await readCart();
      if (hasUnsafeIncompleteBundle(finalCart)) {
        throw new Error('LAF_BUNDLE_CHECKOUT_UNSAFE');
      }
    }
  };

  let checkoutPassThrough = false;

  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const submitter = event.submitter || null;
    const isCheckout = submitter
      ? String(submitter.name || '').toLowerCase() === 'checkout'
      : Boolean(form.querySelector('[name="checkout"]'));

    if (!isCheckout) return;

    if (checkoutPassThrough) {
      checkoutPassThrough = false;
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    if (submitter) submitter.disabled = true;

    try {
      await prepareCheckout();
      checkoutPassThrough = true;

      if (submitter && typeof form.requestSubmit === 'function') {
        submitter.disabled = false;
        form.requestSubmit(submitter);
      } else {
        HTMLFormElement.prototype.submit.call(form);
      }
    } catch (error) {
      checkoutPassThrough = false;
      if (submitter) submitter.disabled = false;
      console.warn('[LAF] Checkout bundle verification:', error && error.message ? error.message : error);
      window.alert('Votre panier vient d’être mis à jour. Merci de cliquer à nouveau sur Commander.');
    }
  }, true);

  document.addEventListener('click', async (event) => {
    const checkoutLink = event.target && event.target.closest
      ? event.target.closest('a[href*="/checkout"]')
      : null;
    if (!checkoutLink || checkoutPassThrough) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    try {
      await prepareCheckout();
      checkoutPassThrough = true;
      window.location.assign(checkoutLink.href);
    } catch (error) {
      checkoutPassThrough = false;
      console.warn('[LAF] Checkout link bundle verification:', error && error.message ? error.message : error);
      window.alert('Votre panier vient d’être mis à jour. Merci de cliquer à nouveau sur Commander.');
    }
  }, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => schedule(60), { once: true });
  } else {
    schedule(60);
  }

  window.addEventListener('pageshow', () => schedule(80));
})();